import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, ChevronDown, GraduationCap } from "lucide-react";
import { useI18n } from "../i18n";
import NidaLogo from "./NidaLogo";

const navItems = [
    { id: "services", key: "nav.services", route: "/servicios" },
    { id: "value", key: "nav.value" },
    { id: "cases", key: "nav.cases" },
    { id: "process", key: "nav.process" },
    { id: "trust", key: "nav.trust" },
];

export default function Navbar() {
    const { t, lang, setLang } = useI18n();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const isAdmin = location.pathname.startsWith("/admin");
    const isHome = location.pathname === "/";

    const goToSection = (id) => (e) => {
        if (isHome) {
            e.preventDefault();
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            // Navigate to home then scroll
            e.preventDefault();
            navigate(`/#${id}`);
            setTimeout(() => {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 100);
        }
        setOpen(false);
    };

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled || open
                    ? "backdrop-blur-xl bg-black/60 border-b border-white/5"
                    : "bg-transparent"
            }`}
        >
            <div className="container-page flex h-16 md:h-20 items-center justify-between">
                <Link to="/" data-testid="nav-logo" className="flex items-center">
                    <NidaLogo className="h-6 md:h-7" showWordmark={true} />
                </Link>

                {!isAdmin && (
                    <nav className="hidden lg:flex items-center gap-7">
                        {navItems.filter((item) => item.id === "services").map((item) =>
                            item.route ? (
                                <Link
                                    key={item.id}
                                    to={item.route}
                                    data-testid={`nav-link-${item.id}`}
                                    className="text-sm text-white/70 hover:text-white transition-colors"
                                >
                                    {t(item.key)}
                                </Link>
                            ) : null
                        )}

                        <div className="relative group">
                        <button
                            type="button"
                            data-testid="nav-sectors"
                            aria-haspopup="true"
                            className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors"
                        >
                            {t("nav.sectors")}
                            <ChevronDown className="h-4 w-4" />
                        </button>
                        <div className="absolute left-0 top-full pt-3 hidden group-hover:block group-focus-within:block">
                            <div className="w-56 rounded-xl border border-white/10 bg-[#101010] p-2 shadow-2xl">
                                <Link
                                    to="/sectores/educacion"
                                    data-testid="nav-sector-educacion"
                                    className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm text-white/80 hover:bg-white/10 hover:text-white"
                                >
                                    <GraduationCap className="h-4 w-4 text-brand-glow" />
                                    {t("nav.education")}
                                </Link>
                                <div className="px-3 py-3 text-xs text-white/35">
                                    {t("nav.industrySoon")}
                                </div>
                            </div>
                        </div>
                    </div>

                        {navItems.filter((item) => item.id !== "services").map((item) =>
                            item.route ? (
                                <Link
                                    key={item.id}
                                    to={item.route}
                                    data-testid={`nav-link-${item.id}`}
                                    className="text-sm text-white/70 hover:text-white transition-colors"
                                >
                                    {t(item.key)}
                                </Link>
                            ) : (
                                <a
                                    key={item.id}
                                    href={`/#${item.id}`}
                                    onClick={goToSection(item.id)}
                                    data-testid={`nav-link-${item.id}`}
                                    className="text-sm text-white/70 hover:text-white transition-colors"
                                >
                                    {t(item.key)}
                                </a>
                            )
                        )}
                    </nav>
                )}


                <div className="flex items-center gap-2">
                    <div className="hidden sm:flex items-center rounded-full border border-white/10 bg-white/5 p-0.5 text-xs">
                        <Globe className="h-3.5 w-3.5 mx-2 text-white/50" />
                        <button
                            data-testid="lang-es"
                            onClick={() => setLang("es")}
                            className={`px-2.5 py-1 rounded-full transition ${
                                lang === "es" ? "bg-white text-black" : "text-white/60 hover:text-white"
                            }`}
                        >
                            ES
                        </button>
                        <button
                            data-testid="lang-en"
                            onClick={() => setLang("en")}
                            className={`px-2.5 py-1 rounded-full transition ${
                                lang === "en" ? "bg-white text-black" : "text-white/60 hover:text-white"
                            }`}
                        >
                            EN
                        </button>
                    </div>

                    {!isAdmin && (
                        <a
                            href="/#contact"
                            onClick={goToSection("contact")}
                            data-testid="nav-cta-button"
                            className="hidden md:inline-flex btn-primary py-2 px-5"
                        >
                            {t("nav.cta")}
                        </a>
                    )}

                    {!isAdmin && (
                        <button
                            data-testid="mobile-menu-button"
                            onClick={() => setOpen(!open)}
                            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5"
                            aria-label="Toggle menu"
                        >
                            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                        </button>
                    )}
                </div>
            </div>

            {open && !isAdmin && (
                <div className="lg:hidden border-t border-white/5 bg-black/80 backdrop-blur-xl">
                    <div className="container-page py-6 flex flex-col gap-4">
                        {navItems.filter((item) => item.id === "services").map((item) => (
                            <Link
                                key={item.id}
                                to={item.route}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-link-${item.id}`}
                                className="text-base text-white/80 hover:text-white"
                            >
                                {t(item.key)}
                            </Link>
                        ))}

                        <details className="group">
                            <summary
                                data-testid="mobile-nav-sectors"
                                className="flex cursor-pointer list-none items-center justify-between text-base text-white/80 hover:text-white [&::-webkit-details-marker]:hidden"
                            >
                                {t("nav.sectors")}
                                <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                            </summary>

                            <div className="mt-4 flex flex-col gap-4 border-l border-white/10 pl-4">
                                <Link
                                    to="/sectores/educacion"
                                    onClick={() => setOpen(false)}
                                    data-testid="mobile-nav-sector-educacion"
                                    className="flex items-center gap-2 text-base text-white/80 hover:text-white"
                                >
                                    <GraduationCap className="h-4 w-4 text-brand-glow" />
                                    {t("nav.education")}
                                </Link>

                                <p className="text-sm text-white/35">
                                    {t("nav.industrySoon")}
                                </p>
                            </div>
                        </details>

                        {navItems.filter((item) => item.id !== "services").map((item) =>
                            item.route ? (
                                <Link
                                    key={item.id}
                                    to={item.route}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-link-${item.id}`}
                                    className="text-base text-white/80 hover:text-white"
                                >
                                    {t(item.key)}
                                </Link>
                            ) : (
                                <a
                                    key={item.id}
                                    href={`/#${item.id}`}
                                    onClick={goToSection(item.id)}
                                    data-testid={`mobile-nav-link-${item.id}`}
                                    className="text-base text-white/80 hover:text-white"
                                >
                                    {t(item.key)}
                                </a>
                            )
                        )}
                        <a
                            href="/#contact"
                            onClick={goToSection("contact")}
                            data-testid="mobile-nav-cta"
                            className="btn-primary mt-2 self-start"
                        >
                            {t("nav.cta")}
                        </a>
                        <div className="flex items-center gap-2 mt-2 sm:hidden">
                            <button
                                onClick={() => setLang("es")}
                                className={`px-3 py-1 rounded-full text-xs ${
                                    lang === "es" ? "bg-white text-black" : "border border-white/15 text-white/70"
                                }`}
                            >
                                ES
                            </button>
                            <button
                                onClick={() => setLang("en")}
                                className={`px-3 py-1 rounded-full text-xs ${
                                    lang === "en" ? "bg-white text-black" : "border border-white/15 text-white/70"
                                }`}
                            >
                                EN
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
